import React from 'react';
import { Button, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams, Link } from 'react-router-dom';

import Dialog from '../../../DesignSystem/Dialog';
import { postTypePlural } from '../../../../Utils/Types';
import { isIdea, isAnomaly, isMessage } from '../../../../Utils/Post';
import useSpace from '../../../../Hooks/useSpace';
import usePinUnpinMutation from '../../../../Hooks/usePinUnpinMutation';
import ButtonWithLoader from '../../../ButtonWithLoader';
import { Post } from '../../../../Utils/Types/Posts';
import useCurrentUser from '../../../../Hooks/useCurrentUser';
import SignUpButton from '../../../NavBar/SignUpButton';

interface PostCreatedModalProps {
  post: Post;
  isOpen?: boolean;
}

function PostCreatedModal({ isOpen = false, post }: PostCreatedModalProps) {
  const postType = post?.type;
  const intl = useIntl();
  const [search, setSearch] = useSearchParams();
  const { space } = useSpace();
  const { currentUser } = useCurrentUser();

  const open = isOpen || search.get('modal') === 'post-created';

  const closeModal = () => {
    setSearch({});
  };

  const { mutate: pinUnpinPost, isLoading: pinUnpinIsFetching } = usePinUnpinMutation({
    post,
    onSuccess: closeModal,
  });

  const pluralType = postTypePlural(postType).toUpperCase();
  const modalTitle = intl.formatMessage({ id: `POST_FORM.NEW.${pluralType}.MODAL_TITLE` });
  const modalDescription =
    isIdea(postType) && !currentUser
      ? intl.formatMessage({ id: 'POST_FORM.NEW.IDEAS.MODAL_DESCRIPTION_GUEST' })
      : intl.formatMessage(
          { id: `POST_FORM.NEW.${pluralType}.MODAL_DESCRIPTION` },
          { isPrivateSpace: !space?.public, spaceName: space.name },
        );

  const renderButtons = () => {
    const understoodButton = (
      <Button size="large" color="primary" onClick={closeModal} variant="text">
        <FormattedMessage id="POST_FORM.UNDERSTOOD" />
      </Button>
    );

    const dismissButton = (
      <Button size="large" color="primary" onClick={closeModal} variant="outlined">
        <FormattedMessage id="BUTTONS.IGNORE" />
      </Button>
    );
    if (isIdea(postType)) {
      if (!currentUser) {
        return (
          <>
            {dismissButton}
            <SignUpButton size="large" />
          </>
        );
      } else {
        return (
          <>
            {understoodButton}
            <Button
              size="large"
              variant="contained"
              color="primary"
              component={Link}
              to={`/${space.slug}/contributions/participation`}
            >
              <FormattedMessage id="POST_FORM.NEW.IDEAS.MODAL_CTA" />
            </Button>
          </>
        );
      }
    }

    if (isAnomaly(postType) || isMessage(postType)) {
      return (
        <>
          {understoodButton}
          <Button
            size="large"
            variant="contained"
            color="primary"
            component={Link}
            to={`/${space.slug}/contributions/messaging`}
          >
            <FormattedMessage id="POST_FORM.NEW.DEMANDS.MODAL_CTA" />
          </Button>
        </>
      );
    }

    return (
      <>
        {understoodButton}
        <ButtonWithLoader
          size="large"
          variant="contained"
          color="primary"
          onClick={pinUnpinPost}
          isFetching={pinUnpinIsFetching}
        >
          <FormattedMessage id="POSTS.CREATE_MENU.PIN" />
        </ButtonWithLoader>
      </>
    );
  };

  if (!open || !post) return null;

  return (
    <Dialog
      id="post-created-modal"
      open={open}
      onClose={closeModal}
      label={modalTitle}
      title={modalTitle}
      actions={renderButtons()}
    >
      <Typography>{modalDescription}</Typography>
    </Dialog>
  );
}

export default PostCreatedModal;
